import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AppRoutes } from '@/routes';
import { initSystemThemeWatcher, useUiStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ConfirmDialogProvider } from '@/components/ConfirmDialog';
import { Toaster } from '@/components/ui/toast';
import { TooltipProvider } from '@/components/ui/tooltip';
import { MAX_IN_FLIGHT } from '@/lib/toast-queue';
import { api } from '@/lib/api-client';
import { applyBoTheme, readBoTheme } from '@/lib/branding/applyBoTheme';
import type { Organization } from '@/pages/tenancy/types';

export function App() {
  // Ensure theme + lang are applied on first paint in case rehydration hasn't run yet.
  useEffect(() => {
    useUiStore.getState()._applyResolvedTheme();
    const off = initSystemThemeWatcher();
    return () => off();
  }, []);

  // Refresh the persisted permission snapshot on boot so a role's permission
  // change reflects on the operator's next page load (no re-login needed).
  useEffect(() => {
    void useAuthStore.getState().refreshPermissions();
  }, []);

  const isStaff = useAuthStore((s) => s.isAuthenticated && s.subjectKind === 'admin');
  const org = useQuery({
    queryKey: ['organizations', 'me'],
    queryFn: () => api.get<Organization>('/api/v1/organizations/me'),
    retry: false,
    enabled: isStaff,
    staleTime: 60_000,
  });

  // Every tier can read its own org here; readBoTheme(org.theme) applies the
  // saved BO chrome, or null (no theme set) → applyBoTheme no-ops → b1fr0st defaults.
  useEffect(() => {
    applyBoTheme(readBoTheme(org.data?.theme));
    // Which brand this tab is showing is what the operator's personal accent is
    // keyed by (#229), and only this query knows it — so wait for the answer.
    // This effect also runs on mount, when `org.data` is still undefined and
    // there is no cached answer to fall back on (`retry: false`, no persister);
    // announcing "no brand" then would repaint the default over the colour
    // rehydration has just restored, and leave the operator's choice lost for
    // the whole session if the request went on to fail.
    if (!org.data) return;
    useUiStore.getState().setAccentOrg(org.data.id);
  }, [org.data]);

  const resolvedTheme = useUiStore((s) => s.resolvedTheme);

  return (
    <ErrorBoundary>
      <TooltipProvider delayDuration={200} skipDelayDuration={100}>
        <ConfirmDialogProvider>
          <AppRoutes />
          <Toaster
            position="top-right"
            richColors
            theme={resolvedTheme}
            visibleToasts={MAX_IN_FLIGHT}
          />
        </ConfirmDialogProvider>
      </TooltipProvider>
    </ErrorBoundary>
  );
}
